"use client";

import * as React from "react";
import { formatPrice } from "../../lib/formatPrice";
import { cn } from "../../lib/utils";
import {
  PRICE_TRENDS_HEATMAP_HUE_MAX,
  PRICE_TRENDS_HEATMAP_HUE_MIN,
  PRICE_TRENDS_HEATMAP_STRIP_STYLES,
} from "./constants";
import type { PriceTrendsHeatmapStripProps } from "./types";

function intensityColor(t: number): string {
  const clamped = Math.min(1, Math.max(0, t));
  const hue =
    PRICE_TRENDS_HEATMAP_HUE_MIN +
    (PRICE_TRENDS_HEATMAP_HUE_MAX - PRICE_TRENDS_HEATMAP_HUE_MIN) * clamped;
  return `hsl(${hue}, 70%, 55%)`;
}

export const PriceTrendsHeatmapStrip = React.forwardRef<
  HTMLElement,
  PriceTrendsHeatmapStripProps
>(
  (
    {
      className,
      title,
      description,
      items,
      currency = "USD",
      cheapestLabel = "Más barato",
      mostExpensiveLabel = "Más caro",
      ...props
    },
    ref
  ) => {
    const { minId, maxId, min, max } = React.useMemo(() => {
      if (items.length === 0) {
        return { minId: null, maxId: null, min: 0, max: 0 };
      }
      let cheapest = items[0];
      let priciest = items[0];
      for (const item of items) {
        if (item.price < cheapest.price) cheapest = item;
        if (item.price > priciest.price) priciest = item;
      }
      return {
        minId: cheapest.id,
        maxId: priciest.id,
        min: cheapest.price,
        max: priciest.price,
      };
    }, [items]);

    const range = max - min;

    return (
      <section
        ref={ref}
        className={cn(PRICE_TRENDS_HEATMAP_STRIP_STYLES.root, className)}
        {...props}
      >
        {(title || description) && (
          <header className={PRICE_TRENDS_HEATMAP_STRIP_STYLES.header}>
            {title && (
              <h2 className={PRICE_TRENDS_HEATMAP_STRIP_STYLES.title}>
                {title}
              </h2>
            )}
            {description && (
              <p className={PRICE_TRENDS_HEATMAP_STRIP_STYLES.description}>
                {description}
              </p>
            )}
          </header>
        )}
        <div
          className={PRICE_TRENDS_HEATMAP_STRIP_STYLES.strip}
          role="list"
        >
          {items.map((item) => {
            const t = range > 0 ? (item.price - min) / range : 0;
            const isMin = item.id === minId;
            const isMax = item.id === maxId;
            const formattedPrice = formatPrice(item.price, currency);
            return (
              <div
                key={item.id}
                role="listitem"
                data-testid={`price-trends-heatmap-strip-cell-${item.id}`}
                data-variant={isMin ? "min" : isMax ? "max" : "default"}
                title={`${item.month}: ${formattedPrice}`}
                className={PRICE_TRENDS_HEATMAP_STRIP_STYLES.cell}
                style={{
                  backgroundColor: intensityColor(t),
                  color: t > 0.55 ? "#fff" : "#1a1a1a",
                }}
              >
                <span className={PRICE_TRENDS_HEATMAP_STRIP_STYLES.cellMonth}>
                  {item.month}
                </span>
                <span className={PRICE_TRENDS_HEATMAP_STRIP_STYLES.cellPrice}>
                  {formattedPrice}
                </span>
                {(isMin || isMax) && (
                  <span
                    aria-label={isMin ? cheapestLabel : mostExpensiveLabel}
                    className={cn(
                      PRICE_TRENDS_HEATMAP_STRIP_STYLES.marker,
                      isMin
                        ? PRICE_TRENDS_HEATMAP_STRIP_STYLES.markerMin
                        : PRICE_TRENDS_HEATMAP_STRIP_STYLES.markerMax
                    )}
                  >
                    {isMin ? "↓" : "↑"}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        <div className={PRICE_TRENDS_HEATMAP_STRIP_STYLES.legend}>
          <span className={PRICE_TRENDS_HEATMAP_STRIP_STYLES.legendItem}>
            <span
              className={cn(
                PRICE_TRENDS_HEATMAP_STRIP_STYLES.legendSwatch,
                PRICE_TRENDS_HEATMAP_STRIP_STYLES.legendSwatchMin
              )}
            />
            {cheapestLabel}
          </span>
          <span className={PRICE_TRENDS_HEATMAP_STRIP_STYLES.legendItem}>
            <span
              className={cn(
                PRICE_TRENDS_HEATMAP_STRIP_STYLES.legendSwatch,
                PRICE_TRENDS_HEATMAP_STRIP_STYLES.legendSwatchMax
              )}
            />
            {mostExpensiveLabel}
          </span>
        </div>
      </section>
    );
  }
);

PriceTrendsHeatmapStrip.displayName = "PriceTrendsHeatmapStrip";
